import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { useCurrency } from "@/contexts/currency";
import {
  AddPhoneNumberParams,
  GeniePhoneNumber,
  PhoneSubscription,
  SubscriptionService,
} from "@/services/billing/SubscriptionService";

const EMPTY_PHONE_SUBSCRIPTIONS: PhoneSubscription[] = [];
const EMPTY_GENIE_PHONE_NUMBERS: GeniePhoneNumber[] = [];

export const usePhoneSubscription = () => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { currencyConfig } = useCurrency();
  const { user } = useAuth();

  const {
    data: phoneBilling,
    isLoading: isLoadingSubscriptions,
    refetch: refetchSubscriptions,
  } = useQuery({
    queryKey: ["phoneSubscriptions"],
    queryFn: () => SubscriptionService.getPhoneBilling(),
    enabled: !!user?.id,
  });
  const phoneSubscriptions = phoneBilling?.phoneSubscriptions ?? EMPTY_PHONE_SUBSCRIPTIONS;
  const geniePhoneNumbers = phoneBilling?.numbers ?? EMPTY_GENIE_PHONE_NUMBERS;

  const addPhoneMutation = useMutation({
    mutationFn: (params: Omit<AddPhoneNumberParams, "priceId"> & { phoneType: "local" | "tollFree" }) => {
      const priceId = params.phoneType === "local" ? currencyConfig.stripePriceIds.phoneLocal : currencyConfig.stripePriceIds.phoneTollFree;

      return SubscriptionService.addPhoneNumber({
        ...params,
        priceId,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["phoneSubscriptions"] });
      // Invalidate agent queries to refresh phone data in parent components
      if (variables.agentId) {
        queryClient.invalidateQueries({ queryKey: ["agent-details", variables.agentId] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", variables.agentId] });
      }
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const removePhoneMutation = useMutation({
    mutationFn: (phoneNumber: string) => SubscriptionService.removePhoneNumber(phoneNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phoneSubscriptions"] });
      // Invalidate agent queries to refresh phone data in parent components
      queryClient.invalidateQueries({ queryKey: ["agent-details"] });
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["user-agents"] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const addPhoneNumber = useCallback(
    async (params: { phoneNumber: string; phoneType: "local" | "tollFree"; agentId?: string }) => {
      return addPhoneMutation.mutateAsync(params);
    },
    [addPhoneMutation],
  );

  const removePhoneNumber = useCallback(
    async (subscriptionItemId: string) => {
      return removePhoneMutation.mutateAsync(subscriptionItemId);
    },
    [removePhoneMutation],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    phoneSubscriptions,
    geniePhoneNumbers,
    isLoadingSubscriptions,
    refetchSubscriptions,
    addPhoneNumber,
    removePhoneNumber,
    isAddingPhone: addPhoneMutation.isPending,
    isRemovingPhone: removePhoneMutation.isPending,
    error,
    clearError,
  };
};
